/**
 * Mentor setup checklist: card states and "do it now" deep links (founder QA F1/F2, 2026-10-16).
 *
 * The server derives each step from the mentor's own rows
 * (`GET /mentor/engagements/setup-checklist`); this module only decides what
 * the card shows and where each step opens. Copy lives in `mentorChecklist.*`.
 */
import { ROUTES } from '../../navigation/routes';
import type {
  MentorChecklistStep,
  MentorChecklistStepKey,
  MentorSetupChecklist,
} from '@/types/mentorEngagements';

/** Mentor intake uses whose onboarding plan is the mentor workspace (mirrors MENTOR_WORKSPACE_USES). */
export const WORKSPACE_MENTOR_USES: readonly string[] = [
  'mentee_sessions',
  'mentoring_others',
  'feedback_prep',
  'program',
  'both',
];
const COHORT_USES: readonly string[] = ['program', 'both'];
const BASE_STEPS: MentorChecklistStepKey[] = [
  'create_engagement',
  'add_session',
  'review_sof',
  'finalize_crf',
  'send_forms',
];

export const CHECKLIST_STEP_ICONS: Record<MentorChecklistStepKey, string> = {
  create_engagement: 'person-add-alt',
  add_session: 'event-note',
  review_sof: 'fact-check',
  finalize_crf: 'verified',
  send_forms: 'forward-to-inbox',
  link_cohort: 'school',
};

type StoredPlan = { source?: string | null; plan_kind?: string | null } | null | undefined;

/** A stored mentor onboarding plan (the mentoring plan or its workspace fallback): the checklist shows with it. */
export function isMentorWorkspacePlan(plan: StoredPlan): boolean {
  return plan?.source === 'onboarding_mentor';
}

/**
 * The mentor's personal mentoring plan: shown as a plan card next to the live checklist. A mentor
 * onboarding plan without this kind is the deterministic workspace fallback (or a legacy row): its
 * steps are the checklist, so only the checklist shows.
 */
export function isMentorCraftPlan(plan: StoredPlan): boolean {
  return isMentorWorkspacePlan(plan) && plan?.plan_kind === 'mentor_plan';
}

const STEP_KEYS: readonly string[] = [...BASE_STEPS, 'link_cohort'];

/** A stored "Start here" step key, or null when it is not a checklist step. */
export function asChecklistStepKey(key: string | null | undefined): MentorChecklistStepKey | null {
  return key && STEP_KEYS.includes(key) ? (key as MentorChecklistStepKey) : null;
}

export type StartHere =
  | { kind: 'step'; step: MentorChecklistStepKey; href: ChecklistHref | null; personal: boolean }
  | { kind: 'complete' };

/**
 * What the mentor plan's "Start here" shows. The live checklist wins: its next step, with that step's
 * "do it now" link. The plan's own first action (written for the step that was next when the plan was
 * made) shows only while that step is still next (`personal`), so the text never goes stale. Until the
 * checklist loads, or when it is locked or failed, the stored step shows without a link.
 */
export function resolveStartHere(storedStep: string | null | undefined, state: ChecklistCardState): StartHere {
  const stored = asChecklistStepKey(storedStep);
  if (state.kind === 'complete') return { kind: 'complete' };
  if (state.kind === 'progress') {
    return { kind: 'step', step: state.next.key, href: checklistStepHref(state.next), personal: state.next.key === stored };
  }
  const step = stored ?? BASE_STEPS[0];
  return { kind: 'step', step, href: null, personal: stored != null };
}

/** The onboarding plan is the deterministic mentor workspace (no LLM, no loading animation). */
export function isWorkspaceMentorUse(mentorUse: string | null | undefined): boolean {
  return Boolean(mentorUse && WORKSPACE_MENTOR_USES.includes(mentorUse));
}

/** A not-started checklist for surfaces that cannot call the server (onboarding reveal, locked plan). */
export function previewChecklist(mentorUse: string | null | undefined, hasSchool: boolean): MentorSetupChecklist {
  const keys = mentorUse && COHORT_USES.includes(mentorUse) && hasSchool
    ? [...BASE_STEPS, 'link_cohort' as const]
    : BASE_STEPS;
  return {
    applicable: true,
    steps: keys.map((key) => ({ key, done: false, engagement_id: null, session_id: null })),
    completed: 0,
    total: keys.length,
    next_step: keys[0],
  };
}

export type ChecklistCardState =
  | { kind: 'hidden' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'locked'; checklist: MentorSetupChecklist }
  | { kind: 'progress'; checklist: MentorSetupChecklist; next: MentorChecklistStep }
  | { kind: 'complete'; checklist: MentorSetupChecklist };

export interface ChecklistCardInput {
  /** The server grants Mentees (tier3); false shows the locked preview instead of calling the API. */
  entitled: boolean;
  /** The plan the card replaces is the mentor workspace plan. */
  mentorPlan: boolean;
  loading: boolean;
  failed: boolean;
  checklist: MentorSetupChecklist | null;
  mentorUse?: string | null;
  hasSchool?: boolean;
}

export function checklistCardState(input: ChecklistCardInput): ChecklistCardState {
  if (!input.entitled) {
    return input.mentorPlan
      ? { kind: 'locked', checklist: previewChecklist(input.mentorUse, Boolean(input.hasSchool)) }
      : { kind: 'hidden' };
  }
  if (input.checklist) {
    if (!input.checklist.applicable || input.checklist.steps.length === 0) return { kind: 'hidden' };
    const next = input.checklist.steps.find((step) => !step.done);
    return next
      ? { kind: 'progress', checklist: input.checklist, next }
      : { kind: 'complete', checklist: input.checklist };
  }
  if (input.failed) return input.mentorPlan ? { kind: 'error' } : { kind: 'hidden' };
  return input.loading && input.mentorPlan ? { kind: 'loading' } : { kind: 'hidden' };
}

export type ChecklistHref = { pathname: string; params: Record<string, string> };

/** Where a step's "do it now" opens: the exact screen, modal or tab for the next action. */
export function checklistStepHref(step: Pick<MentorChecklistStep, 'key' | 'engagement_id' | 'session_id'>): ChecklistHref {
  const newMentee = { pathname: ROUTES.MENTOR_ENGAGEMENTS, params: { new: '1' } };
  if (step.key === 'create_engagement' || step.engagement_id == null) return newMentee;
  const detail = (params: Record<string, string>) => ({
    pathname: ROUTES.MENTOR_ENGAGEMENT_DETAIL,
    params: { id: String(step.engagement_id), ...params },
  });
  switch (step.key) {
    case 'add_session': return detail({ action: 'add_session' });
    case 'review_sof':
      return detail(step.session_id != null
        ? { action: 'review_sof', session: String(step.session_id) }
        : { action: 'add_session' });
    case 'finalize_crf': return detail({ action: 'crf' });
    case 'send_forms': return detail({ action: 'send' });
    case 'link_cohort': return detail({ action: 'share' });
    default: return newMentee;
  }
}

/** The completed card's next useful action: start the next mentee. */
export function checklistCompleteHref(): ChecklistHref {
  return { pathname: ROUTES.MENTOR_ENGAGEMENTS, params: { new: '1' } };
}

export type WorkspaceDeepLink =
  | { tab: 'sessions'; open: 'add_session' }
  | { tab: 'sessions'; open: 'review_sof'; sessionId: number }
  | { tab: 'documents'; open: 'crf' | 'send' }
  | { tab: 'overview'; open: 'share' };

/** Parse the workspace's `?action=` deep link; unknown or incomplete links do nothing. */
export function workspaceDeepLink(action: string | undefined, session: string | undefined): WorkspaceDeepLink | null {
  switch (action) {
    case 'add_session': return { tab: 'sessions', open: 'add_session' };
    case 'review_sof': {
      const sessionId = Number(session);
      return Number.isInteger(sessionId) && sessionId > 0 ? { tab: 'sessions', open: 'review_sof', sessionId } : null;
    }
    case 'crf': return { tab: 'documents', open: 'crf' };
    case 'send': return { tab: 'documents', open: 'send' };
    case 'share': return { tab: 'overview', open: 'share' };
    default: return null;
  }
}
